import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Button, Dialog, Form, QueryResult, Toast } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { useFileUploadChecks } from "Hooks";
import {
  ImportableVersionsSelection,
  getImportableVersions,
  getSelectionSize,
  getVersionsSelectionSchema,
} from "Modules/importableVersions";
import { useGetExternalCatalogPackageQuery } from "Services";
import { ExternalPackageInfo } from "Types";
import { apiErrorToast } from "Utils";
import * as yup from "Utils/yup";
import { useImportExternalPackageVersionsMutation } from "../../services/projects.api";
import { ImportExternalPackageVersionsRequest } from "../../types";
import styles from "./importLatest.module.scss";

type ImportLatestFormData = Required<
  Pick<
    ImportExternalPackageVersionsRequest,
    "versions" | "notify_on_completion"
  >
>;

type ImportLatestProps = {
  packageId: string;
  packageName: string;
  groupId: string;
  externalPackage: ExternalPackageInfo;
  importedVersions: string[];
  isOpen: boolean;
  onClose: () => void;
};

type ImportLatestFormProps = Omit<ImportLatestProps, "isOpen">;

const ImportLatestForm = ({
  packageId,
  packageName,
  groupId,
  externalPackage,
  importedVersions,
  onClose,
}: ImportLatestFormProps): React.JSX.Element => {
  const {
    data: versions,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetExternalCatalogPackageQuery({
    catalog: externalPackage.catalog,
    externalPackageId: externalPackage.id,
    groupId,
  });
  const { remainingCapacity, maxVersions } = useFileUploadChecks(
    "VERSION",
    null,
  );
  const [importVersions, { isLoading: isImporting }] =
    useImportExternalPackageVersionsMutation();

  const newVersions = getImportableVersions(
    versions?.versions.filter(
      (version) => !importedVersions.includes(version.version),
    ) ?? [],
    externalPackage,
  );

  const schema: yup.SchemaOf<ImportLatestFormData> = yup.object({
    versions: getVersionsSelectionSchema(
      newVersions,
      remainingCapacity,
      maxVersions,
    ),
    notify_on_completion: yup.boolean().required(),
  });
  const methods = useForm<ImportLatestFormData>({
    resolver: yupResolver(schema),
    values: {
      versions: newVersions.slice(0, maxVersions).map((item) => item.version),
      notify_on_completion: false,
    },
  });

  const isOverCapacity =
    getSelectionSize(newVersions, methods.watch("versions")) >
    remainingCapacity;

  const handleImport = async (data: ImportLatestFormData): Promise<void> => {
    try {
      await importVersions({
        catalog: externalPackage.catalog,
        packageId,
        ...data,
      }).unwrap();

      Toast.show({
        title: (
          <>
            Importing {data.versions.length} version
            {data.versions.length === 1 ? "" : "s"} into{" "}
            <strong>{packageName}</strong>
          </>
        ),
        type: "success",
      });
      onClose();
    } catch (importError) {
      apiErrorToast(importError, "Could not import versions");
    }
  };

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>
          Import latest versions from{" "}
          <em>{externalPackage.catalog_display_name}</em> catalog
        </Dialog.Header.Title>
        <Dialog.Header.Subtitle>{packageName}</Dialog.Header.Subtitle>
      </Dialog.Header>
      <FormProvider {...methods}>
        <Form.Form onSubmit={methods.handleSubmit(handleImport)}>
          <Dialog.Body className={styles.body}>
            <QueryResult
              status={getQueryStatus({
                isLoading,
                isFetching,
                isError,
                error,
                data: versions,
              })}
            >
              <Controller
                control={methods.control}
                name="versions"
                render={({ field, fieldState }) => (
                  <>
                    <ImportableVersionsSelection
                      catalogName={externalPackage.catalog_display_name}
                      versions={newVersions}
                      selected={field.value}
                      onChange={field.onChange}
                      remainingCapacity={remainingCapacity}
                      maxVersions={maxVersions}
                      emptySubject="new versions"
                    />
                    {fieldState.error && (
                      <Form.HelperText error>
                        {fieldState.error.message}
                      </Form.HelperText>
                    )}
                  </>
                )}
              />
              <Dialog.Section>
                <Dialog.Section.Content>
                  <Form.Checkbox
                    name="notify_on_completion"
                    label="Notify me when analysis completes"
                    helperText="Get a notification once every selected version has finished analysis."
                  />
                </Dialog.Section.Content>
              </Dialog.Section>
            </QueryResult>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Footer.Actions>
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              {newVersions.length > 0 && (
                <Button
                  type="submit"
                  disabled={isImporting || isOverCapacity}
                  tooltipTitle={
                    isOverCapacity
                      ? "Selected versions exceed the remaining analysis capacity"
                      : undefined
                  }
                >
                  Import
                </Button>
              )}
            </Dialog.Footer.Actions>
          </Dialog.Footer>
        </Form.Form>
      </FormProvider>
    </>
  );
};

const ImportLatest = ({
  isOpen,
  onClose,
  ...props
}: ImportLatestProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} size="md" onClose={onClose}>
    <ImportLatestForm {...props} onClose={onClose} />
  </Dialog.Frame>
);

export default ImportLatest;
